import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiCheck, FiGlobe, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import Flag from "../../components/common/flag/Flag";
import ChangePasswordSection from "./components/ChangePasswordSection";
import DeleteAccountModal from "./components/DeleteAccountModal";
import { deleteAccount } from "../../services/meApi";
import styles from "./SettingsPage.module.css";

const SettingsPage = () => {
    const { t } = useTranslation();
    const { language, setLanguage, supportedLanguages } = useLanguage();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    // Deleting the whole account (tenant + all data) is owner-only.
    const isOwner = (user?.roles || []).includes("OWNER");

    const handleConfirmDelete = async () => {
        if (deleting) return;
        setDeleteError("");
        setDeleting(true);
        try {
            await deleteAccount();
            // The account (and this user) no longer exists — drop the now-invalid session and leave.
            toast.success(t("settings.deleteAccount.success"));
            setDeleteModalOpen(false);
            await logout();
            navigate("/login", { replace: true });
        } catch (e) {
            setDeleteError(e?.response?.data?.message || t("settings.deleteAccount.failed"));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <FiGlobe size={28} className={styles.headerIcon} />
                <div>
                    <h1 className={styles.title}>{t("settings.title")}</h1>
                    <p className={styles.subtitle}>{t("settings.subtitle")}</p>
                </div>
            </header>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>{t("settings.language")}</h2>
                <p className={styles.sectionDescription}>
                    {t("settings.languageDescription")}
                </p>

                <div className={styles.languageGrid}>
                    {supportedLanguages.map((lng) => {
                        const isActive = lng.code === language;
                        return (
                            <button
                                key={lng.code}
                                type="button"
                                className={`${styles.languageCard} ${isActive ? styles.languageCardActive : ""}`}
                                onClick={() => setLanguage(lng.code)}
                                aria-pressed={isActive}
                            >
                                <span className={styles.flag} aria-hidden="true"><Flag code={lng.code} size={24} /></span>
                                <span className={styles.languageName}>{lng.nativeName}</span>
                                <span className={styles.languageCode}>{lng.code.toUpperCase()}</span>
                                {isActive && (
                                    <span className={styles.activeBadge}>
                                        <FiCheck size={16} />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            <ChangePasswordSection />

            {isOwner && (
                <section className={`${styles.section} ${styles.dangerZone}`}>
                    <div className={styles.dangerHeader}>
                        <FiAlertTriangle size={20} />
                        <h2 className={styles.dangerTitle}>{t("settings.deleteAccount.dangerZoneTitle")}</h2>
                    </div>
                    <p className={styles.sectionDescription}>{t("settings.deleteAccount.dangerZoneDesc")}</p>
                    <button type="button" className={styles.deleteBtn} onClick={() => setDeleteModalOpen(true)}>
                        <FiTrash2 size={16} />
                        {t("settings.deleteAccount.button")}
                    </button>
                </section>
            )}

            <DeleteAccountModal
                isOpen={deleteModalOpen}
                loading={deleting}
                error={deleteError}
                onCancel={() => {
                    if (deleting) return;
                    setDeleteModalOpen(false);
                    setDeleteError("");
                }}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};

export default SettingsPage;
